import React, { useState, useEffect } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Badge } from "../ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../ui/dialog";
import { toast } from "react-toastify";
import { Plus, Edit, Trash2, Save, X, Users, Shield, MessageCircle } from "lucide-react";
import { API } from "./adminConfig";

const CommunityPersonasTab = () => {
  const [personas, setPersonas] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [polls, setPolls] = useState([]);
  const [discussions, setDiscussions] = useState([]);
  const [imageLibrary, setImageLibrary] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [showImagePicker, setShowImagePicker] = useState(false);
  const [editingPersona, setEditingPersona] = useState(null);
  const [selectedPersona, setSelectedPersona] = useState(null);
  const [showPostForm, setShowPostForm] = useState(false);
  const [selectedRoom, setSelectedRoom] = useState('');
  const [formData, setFormData] = useState({
    display_name: '',
    profile_photo: '',
    bio: '',
    age: '',
    location: ''
  });
  const [postData, setPostData] = useState({
    message: '',
    room_id: '',
    parent_message_id: '',
    post_type: 'discussion' // 'discussion' or 'reply'
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchPersonas();
    fetchRooms();
    fetchPolls();
    fetchImageLibrary();
  }, []);

  useEffect(() => {
    if (selectedRoom) {
      fetchDiscussions(selectedRoom);
    }
  }, [selectedRoom]);

  const fetchPersonas = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/admin/community-personas`);
      setPersonas(response.data);
    } catch (error) {
      console.error('Error fetching personas:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchRooms = async () => {
    try {
      const response = await axios.get(`${API}/admin/chat/rooms`);
      setRooms(response.data.filter(r => r.active));
    } catch (error) {
      console.error('Error fetching rooms:', error);
    }
  };

  const fetchImageLibrary = async () => {
    try {
      const response = await axios.get(`${API}/admin/image-library`);
      setImageLibrary(response.data);
    } catch (error) {
      console.error('Error fetching image library:', error);
    }
  };

  const fetchPolls = async () => {
    try {
      const response = await axios.get(`${API}/admin/polls`);
      // Filter for active polls (is_active field)
      setPolls(response.data.filter(p => p.is_active));
    } catch (error) {
      console.error('Error fetching polls:', error);
    }
  };

  const fetchDiscussions = async (roomId) => {
    try {
      const response = await axios.get(`${API}/chat/messages/${roomId}`);
      setDiscussions(response.data || []);
    } catch (error) {
      console.error('Error fetching discussions:', error);
    }
  };

  const handleCreatePersona = async (e) => {
    e.preventDefault();
    if (!formData.display_name.trim()) {
      toast.error('Display name is required');
      return;
    }

    try {
      setSubmitting(true);
      if (editingPersona) {
        await axios.put(`${API}/admin/community-personas/${editingPersona.id}`, formData);
        toast.success('Persona updated!');
      } else {
        await axios.post(`${API}/admin/community-personas`, formData);
        toast.success('Persona created!');
      }
      fetchPersonas();
      resetForm();
    } catch (error) {
      toast.error('Error saving persona');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeletePersona = async (personaId) => {
    if (!window.confirm('Delete this persona?')) return;
    try {
      await axios.delete(`${API}/admin/community-personas/${personaId}`);
      toast.success('Persona deleted');
      fetchPersonas();
      if (selectedPersona?.id === personaId) {
        setSelectedPersona(null);
      }
    } catch (error) {
      toast.error('Error deleting persona');
    }
  };

  const handlePostAsPersona = async (e) => {
    e.preventDefault();
    if (!selectedPersona || !postData.message.trim() || !postData.room_id) {
      toast.error('Please select a persona, room, and enter a message');
      return;
    }

    try {
      setSubmitting(true);
      await axios.post(`${API}/admin/community-personas/${selectedPersona.id}/post`, {
        room_id: postData.room_id,
        message: postData.message,
        parent_message_id: postData.post_type === 'reply' ? postData.parent_message_id : null
      });
      toast.success(`Posted as ${selectedPersona.display_name}!`);
      setPostData({ message: '', room_id: postData.room_id, parent_message_id: '', post_type: 'discussion' });
      fetchDiscussions(postData.room_id);
    } catch (error) {
      toast.error('Error posting message');
    } finally {
      setSubmitting(false);
    }
  };

  const handleVoteAsPersona = async (pollId, optionIndex) => {
    if (!selectedPersona) {
      toast.error('Please select a persona first');
      return;
    }

    try {
      const response = await axios.post(`${API}/admin/community-personas/${selectedPersona.id}/vote`, {
        poll_id: pollId,
        option_index: optionIndex
      });
      if (response.data.success) {
        toast.success(`Voted as ${selectedPersona.display_name}!`);
        fetchPolls();
      } else {
        toast.error(response.data.message || 'Vote failed');
      }
    } catch (error) {
      toast.error('Error voting');
    }
  };

  const handleReactAsPersona = async (messageId, reactionType) => {
    if (!selectedPersona) {
      toast.error('Please select a persona first');
      return;
    }

    try {
      await axios.post(`${API}/admin/community-personas/${selectedPersona.id}/react`, {
        message_id: messageId,
        reaction_type: reactionType
      });
      toast.success(`Reacted as ${selectedPersona.display_name}!`);
      if (selectedRoom) fetchDiscussions(selectedRoom);
    } catch (error) {
      toast.error('Error adding reaction');
    }
  };

  const resetForm = () => {
    setFormData({ display_name: '', profile_photo: '', bio: '', age: '', location: '' });
    setShowCreateForm(false);
    setShowImagePicker(false);
    setEditingPersona(null);
  };

  const editPersona = (persona) => {
    setFormData({
      display_name: persona.display_name,
      profile_photo: persona.profile_photo || '',
      bio: persona.bio || '',
      age: persona.age || '',
      location: persona.location || ''
    });
    setEditingPersona(persona);
    setShowCreateForm(true);
  };

  const selectImageFromLibrary = (imageUrl) => {
    setFormData({...formData, profile_photo: imageUrl});
    setShowImagePicker(false);
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <div className="animate-spin h-8 w-8 border-4 border-gold border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-serif text-burgundy">Community Seed Users</h2>
          <p className="text-burgundy/60 text-sm">Create personas to seed your community with activity</p>
        </div>
        <Button onClick={() => setShowCreateForm(true)} className="bg-burgundy hover:bg-burgundy/80 text-cream">
          <Plus className="h-4 w-4 mr-2" /> New Persona
        </Button>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Left: Personas List */}
        <div className="lg:col-span-1 space-y-4">
          <h3 className="font-semibold text-burgundy">Your Personas</h3>
          {personas.length === 0 ? (
            <Card className="bg-white border-burgundy/20">
              <CardContent className="text-center py-6">
                <Users className="h-12 w-12 mx-auto mb-3 text-burgundy/30" />
                <p className="text-burgundy/60 text-sm">No personas created yet</p>
              </CardContent>
            </Card>
          ) : (
            personas.map((persona) => (
              <Card 
                key={persona.id} 
                className={`bg-white border-burgundy/20 cursor-pointer transition-all ${selectedPersona?.id === persona.id ? 'ring-2 ring-gold' : 'hover:border-gold/50'}`}
                onClick={() => setSelectedPersona(persona)}
              >
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="h-12 w-12 rounded-full bg-gradient-to-br from-burgundy to-gold flex items-center justify-center text-cream text-lg font-bold overflow-hidden">
                      {persona.profile_photo ? (
                        <img src={persona.profile_photo} alt="" className="h-full w-full object-cover" />
                      ) : (
                        persona.display_name.charAt(0).toUpperCase()
                      )}
                    </div>
                    <div className="flex-1">
                      <h4 className="font-semibold text-burgundy">{persona.display_name}</h4>
                      <p className="text-xs text-burgundy/60">
                        {persona.age && `${persona.age}yo`}
                        {persona.age && persona.location && ' • '}
                        {persona.location}
                        {!persona.age && !persona.location && `${persona.post_count || 0} posts`}
                      </p>
                    </div>
                    <div className="flex gap-1">
                      <Button size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); editPersona(persona); }}>
                        <Edit className="h-3 w-3" />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); handleDeletePersona(persona.id); }}>
                        <Trash2 className="h-3 w-3 text-red-500" />
                      </Button>
                    </div>
                  </div>
                  {persona.bio && <p className="text-xs text-burgundy/60 mt-2 line-clamp-2">{persona.bio}</p>}
                </CardContent>
              </Card>
            ))
          )}
        </div>

        {/* Right: Actions Panel */}
        <div className="lg:col-span-2 space-y-6">
          {selectedPersona ? (
            <>
              {/* Selected Persona Header */}
              <Card className="bg-gradient-to-r from-burgundy/10 to-gold/10 border-gold/30">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="h-14 w-14 rounded-full bg-gradient-to-br from-burgundy to-gold flex items-center justify-center text-cream text-xl font-bold overflow-hidden">
                      {selectedPersona.profile_photo ? (
                        <img src={selectedPersona.profile_photo} alt="" className="h-full w-full object-cover" />
                      ) : (
                        selectedPersona.display_name.charAt(0).toUpperCase()
                      )}
                    </div>
                    <div>
                      <h3 className="font-semibold text-burgundy text-lg">Acting as: {selectedPersona.display_name}</h3>
                      <p className="text-sm text-burgundy/60">Select an action below</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Post as Persona */}
              <Card className="bg-white border-burgundy/20">
                <CardHeader className="pb-2">
                  <CardTitle className="text-lg text-burgundy flex items-center gap-2">
                    <MessageSquare className="h-5 w-5" /> Post to Community
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-burgundy mb-1">Room</label>
                      <select 
                        value={postData.room_id}
                        onChange={(e) => { setPostData({...postData, room_id: e.target.value}); setSelectedRoom(e.target.value); }}
                        className="w-full p-2 border border-burgundy/30 rounded-md text-sm"
                      >
                        <option value="">Select room...</option>
                        {rooms.map(room => (
                          <option key={room.id} value={room.id}>{room.name}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-burgundy mb-1">Type</label>
                      <select 
                        value={postData.post_type}
                        onChange={(e) => setPostData({...postData, post_type: e.target.value})}
                        className="w-full p-2 border border-burgundy/30 rounded-md text-sm"
                      >
                        <option value="discussion">New Discussion</option>
                        <option value="reply">Reply to Discussion</option>
                      </select>
                    </div>
                  </div>

                  {postData.post_type === 'reply' && postData.room_id && (
                    <div>
                      <label className="block text-sm font-medium text-burgundy mb-1">Reply to</label>
                      <select 
                        value={postData.parent_message_id}
                        onChange={(e) => setPostData({...postData, parent_message_id: e.target.value})}
                        className="w-full p-2 border border-burgundy/30 rounded-md text-sm"
                      >
                        <option value="">Select discussion...</option>
                        {discussions.map(d => (
                          <option key={d.id} value={d.id}>{d.message?.substring(0, 60)}...</option>
                        ))}
                      </select>
                    </div>
                  )}

                  <div>
                    <label className="block text-sm font-medium text-burgundy mb-1">Message</label>
                    <textarea
                      value={postData.message}
                      onChange={(e) => setPostData({...postData, message: e.target.value})}
                      placeholder={postData.post_type === 'discussion' ? "Start a new discussion..." : "Write a reply..."}
                      className="w-full p-3 border border-burgundy/30 rounded-md text-sm"
                      rows={4}
                    />
                  </div>

                  <Button 
                    onClick={handlePostAsPersona}
                    disabled={submitting || !postData.message.trim() || !postData.room_id}
                    className="w-full bg-burgundy hover:bg-burgundy/80 text-cream"
                  >
                    {submitting ? 'Posting...' : `Post as ${selectedPersona.display_name}`}
                  </Button>
                </CardContent>
              </Card>

              {/* Vote on Polls */}
              {polls.length > 0 && (
                <Card className="bg-white border-burgundy/20">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-lg text-burgundy flex items-center gap-2">
                      <BarChart3 className="h-5 w-5" /> Vote on Polls
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {polls.slice(0, 3).map(poll => (
                      <div key={poll.id} className="border border-burgundy/20 rounded-lg p-4">
                        <h4 className="font-medium text-burgundy mb-3">{poll.question}</h4>
                        <div className="space-y-2">
                          {poll.options?.map((option, idx) => {
                            // Handle both string options and object options
                            const optionText = typeof option === 'string' ? option : option.text;
                            const voteCount = poll.vote_counts?.[idx] || (typeof option === 'object' ? option.votes : 0) || 0;
                            return (
                              <button
                                key={idx}
                                onClick={() => handleVoteAsPersona(poll.id, idx)}
                                className="w-full text-left p-2 border border-burgundy/20 rounded hover:bg-gold/10 hover:border-gold/50 transition-colors text-sm"
                              >
                                <span>{optionText}</span>
                                <span className="float-right text-burgundy/60">{voteCount} votes</span>
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                  </CardContent>
                </Card>
              )}

              {/* React to Messages */}
              {selectedRoom && discussions.length > 0 && (
                <Card className="bg-white border-burgundy/20">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-lg text-burgundy flex items-center gap-2">
                      👍 React to Messages
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3 max-h-64 overflow-y-auto">
                    {discussions.slice(0, 5).map(msg => (
                      <div key={msg.id} className="border border-burgundy/20 rounded-lg p-3">
                        <p className="text-sm text-burgundy/80 mb-2">{msg.message?.substring(0, 100)}...</p>
                        <div className="flex gap-2">
                          <Button size="sm" variant="outline" onClick={() => handleReactAsPersona(msg.id, 'thumbs_up')}>
                            👍 {msg.reactions?.thumbs_up || 0}
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => handleReactAsPersona(msg.id, 'heart')}>
                            ❤️ {msg.reactions?.heart || 0}
                          </Button>
                        </div>
                      </div>
                    ))}
                  </CardContent>
                </Card>
              )}
            </>
          ) : (
            <Card className="bg-white border-burgundy/20">
              <CardContent className="text-center py-12">
                <Users className="h-16 w-16 mx-auto mb-4 text-burgundy/30" />
                <p className="text-burgundy/60">Select a persona from the list to start posting</p>
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {/* Create/Edit Persona Modal */}
      {showCreateForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-lg bg-white max-h-[90vh] overflow-y-auto">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-burgundy">{editingPersona ? 'Edit Persona' : 'Create New Persona'}</CardTitle>
                <Button variant="ghost" onClick={resetForm} className="h-8 w-8 p-0">
                  <X className="h-4 w-4" />
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleCreatePersona} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-burgundy mb-1">Display Name *</label>
                  <Input
                    value={formData.display_name}
                    onChange={(e) => setFormData({...formData, display_name: e.target.value})}
                    placeholder="e.g., Sarah M., wellness_mom2024"
                    required
                  />
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-burgundy mb-1">Age</label>
                    <Input
                      type="number"
                      value={formData.age}
                      onChange={(e) => setFormData({...formData, age: e.target.value})}
                      placeholder="e.g., 32"
                      min="18"
                      max="99"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-burgundy mb-1">Location</label>
                    <Input
                      value={formData.location}
                      onChange={(e) => setFormData({...formData, location: e.target.value})}
                      placeholder="e.g., Los Angeles, CA"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-burgundy mb-1">Profile Photo</label>
                  <div className="flex gap-2">
                    <Input
                      value={formData.profile_photo}
                      onChange={(e) => setFormData({...formData, profile_photo: e.target.value})}
                      placeholder="https://... or choose from library"
                      className="flex-1"
                    />
                    <Button 
                      type="button" 
                      variant="outline" 
                      onClick={() => setShowImagePicker(true)}
                      className="whitespace-nowrap"
                    >
                      📷 Library
                    </Button>
                  </div>
                  {formData.profile_photo && (
                    <div className="mt-2">
                      <img 
                        src={formData.profile_photo} 
                        alt="Preview" 
                        className="h-16 w-16 rounded-full object-cover border-2 border-burgundy/20"
                      />
                    </div>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-burgundy mb-1">Bio (optional)</label>
                  <textarea
                    value={formData.bio}
                    onChange={(e) => setFormData({...formData, bio: e.target.value})}
                    placeholder="Short bio for authenticity..."
                    className="w-full p-2 border border-burgundy/30 rounded-md text-sm"
                    rows={2}
                  />
                </div>
                <div className="flex gap-3 pt-2">
                  <Button type="button" variant="outline" onClick={resetForm} className="flex-1">Cancel</Button>
                  <Button type="submit" disabled={submitting} className="flex-1 bg-burgundy hover:bg-burgundy/80 text-cream">
                    {submitting ? 'Saving...' : (editingPersona ? 'Update' : 'Create')}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Image Library Picker Modal */}
      {showImagePicker && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4">
          <Card className="w-full max-w-2xl bg-white max-h-[80vh] overflow-hidden">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-burgundy">Choose Profile Photo from Library</CardTitle>
                <Button variant="ghost" onClick={() => setShowImagePicker(false)} className="h-8 w-8 p-0">
                  <X className="h-4 w-4" />
                </Button>
              </div>
            </CardHeader>
            <CardContent className="overflow-y-auto max-h-[60vh]">
              {imageLibrary.length === 0 ? (
                <p className="text-center text-burgundy/60 py-8">No images in library. Upload some images first.</p>
              ) : (
                <div className="grid grid-cols-4 gap-3">
                  {imageLibrary.map((image, idx) => (
                    <div 
                      key={idx}
                      className="cursor-pointer border-2 border-transparent hover:border-gold rounded-lg overflow-hidden transition-all"
                      onClick={() => selectImageFromLibrary(image.url)}
                    >
                      <img 
                        src={image.url} 
                        alt={image.alt || 'Library image'} 
                        className="w-full h-24 object-cover"
                      />
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

// Referrals Tracking Tab Component

export default CommunityPersonasTab;
